"use client";

import Image from "next/image";
import Link from "next/link";
import womanImage from "@/assets/woman2.png";
import checkedIcon from "@/assets/checked.svg";

interface Benefit {
  id: number;
  title: string;
  description: string;
  metric?: string;
}

const benefits: Benefit[] = [
  {
    id: 1,
    title: "Medical Excellence",
    description:
      "Registered healthcare professionals with specialised weight loss expertise",
    metric: "94% Success Rate",
  },
  {
    id: 2,
    title: "Convenient Care",
    description: "Online consultations and 24/7 medical support from anywhere",
    metric: "24/7 Available",
  },
  {
    id: 3,
    title: "Proven Results",
    description:
      "MHRA-approved treatments with scientifically-backed methodologies",
    metric: "50K+ Transformations",
  },
  {
    id: 4,
    title: "Personalised Approach",
    description:
      "Customised treatment plans tailored to your unique health profile",
    metric: "100% Personalised",
  },
];

export default function WhyChooseUsSection() {
  return (
    <section className="relative py-16 sm:py-20 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Premium Content Section */}
          <div className="order-2 lg:order-1 space-y-8">
            {/* Premium Section Badge */}
            <div className="inline-flex items-center space-x-2.5 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full border border-gray-200/60 shadow-sm">
              <div className="w-2 h-2 bg-[#7771B8] rounded-full animate-pulse shadow-sm shadow-[#7771B8]/50"></div>
              <span className="text-gray-700 font-semibold text-sm tracking-wide">
                Why Choose UKWeightloss
              </span>
            </div>

            {/* Sophisticated Typography */}
            <div className="space-y-4">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 leading-[1.1] font-[family-name:var(--font-playfair)]">
                Medical authority{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7771B8] via-[#9d97d8] to-[#7771B8]">
                  you trust
                </span>
              </h2>

              <p className="text-lg sm:text-xl text-gray-500 leading-relaxed font-light">
                The UK&apos;s most trusted medical weight loss authority.
                Experience the difference of working with registered
                professionals who prioritise your health and results.
              </p>
            </div>

            {/* Premium Benefits Grid */}
            <div className="space-y-4">
              {benefits.map((benefit, _) => (
                <div
                  key={benefit.id}
                  className="group bg-white p-6 rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(119,113,184,0.15)] transition-all duration-700 border border-gray-100/80 hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    {/* Premium Check Icon */}
                    <div className="flex-shrink-0 relative mt-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#7771B8]/10 to-purple-100/50 rounded-xl flex items-center justify-center border border-[#7771B8]/10 transform group-hover:scale-110 transition-transform duration-500">
                        <Image
                          src={checkedIcon}
                          alt="Verified"
                          width={16}
                          height={16}
                          className="text-[#7771B8]"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#7771B8] transition-colors duration-300 font-[family-name:var(--font-playfair)]">
                            {benefit.title}
                          </h3>
                          <p className="text-gray-500 leading-relaxed text-base font-light">
                            {benefit.description}
                          </p>
                        </div>

                        {/* Metric Badge */}
                        {benefit.metric && (
                          <div className="flex-shrink-0 self-start">
                            <div className="bg-gray-50 px-3 lg:px-4 py-1.5 rounded-full border border-gray-200 inline-block">
                              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                {benefit.metric}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Premium CTA Section */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Link
                href="/consultation/GUHMJBNGE33R7CJ"
                className="group relative overflow-hidden bg-gradient-to-r from-[#7771B8] via-[#8679c4] to-[#7771B8] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#7771B8]/20 hover:shadow-xl hover:shadow-[#7771B8]/30 transition-all duration-500 hover:scale-[1.02] inline-block"
              >
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <span>Start your journey</span>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#6860a3] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Link>

              <Link
                href="/about"
                className="group text-center text-gray-500 hover:text-[#7771B8] font-medium transition-all duration-300 px-8 py-4"
              >
                <span className="flex items-center justify-center gap-1.5">
                  Learn more
                  <svg
                    className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </Link>
            </div>
          </div>

          {/* Sophisticated Image Section */}
          <div className="order-1 lg:order-2 relative">
            {/* Premium Image Container */}
            <div className="relative group">
              {/* Main Image with Premium Styling */}
              <div className="relative overflow-hidden rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.08)]">
                <div className="aspect-[4/5] sm:aspect-[5/6] lg:aspect-[3/4] relative">
                  <Image
                    src={womanImage}
                    alt="Medical professional consultation"
                    fill
                    className="object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                    priority
                  />

                  {/* Image Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                </div>
              </div>

              {/* Floating Achievement Card - Premium refinement */}
              <div className="absolute -bottom-6 -left-4 lg:-bottom-8 lg:-left-8 bg-white/98 backdrop-blur-md p-5 rounded-xl shadow-[0_4px_32px_rgba(0,0,0,0.08)] border border-gray-100/80 max-w-[200px] lg:max-w-[240px] group-hover:scale-105 transition-transform duration-500">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#7771B8]/10 to-purple-100/50 rounded-xl flex items-center justify-center border border-[#7771B8]/10">
                      <svg
                        className="w-6 h-6 text-[#7771B8]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl lg:text-3xl font-semibold text-transparent bg-clip-text bg-gradient-to-br from-[#7771B8] to-purple-500 mb-0.5 font-[family-name:var(--font-playfair)]">
                      10+
                    </div>
                    <div className="text-xs font-medium text-gray-500 tracking-wide">
                      Years Excellence
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
